'use client'
import React from 'react'
import { memo, useEffect, useState, useRef } from 'react'
import type { FC, ReactNode } from 'react'
import Flickity, { type ReactFlickityComponentProps } from 'react-flickity-component'
import type FlickityInstance from 'flickity'
import { useInView } from 'motion/react'
import { iOS } from '@/hooks'

interface Props {
  children: ReactNode,
  className?: string,
  reloadOnUpdate?: boolean,
  dynamicSlider?: boolean,
  elementType?: 'div' | 'section' | 'aside' | 'dd' | 'ul',
  options?: ReactFlickityComponentProps['options'],
  onChange?: (params: unknown) => void,
  hasCustomDots?: boolean
  colorDots?: 'white' | '#3C3C3B',
  reverse?: boolean
  customAutoPlay?: boolean
  customAutoPlaySpeed?: number
}

const defaultOptions: ReactFlickityComponentProps['options'] = {
  accessibility: false,
  autoPlay: false,
  cellAlign: 'left',
  contain: true,
  draggable: true,
  dragThreshold: 5,
  freeScroll: true,
  freeScrollFriction: 0.075,
  friction: 0.25,
  groupCells: false,
  initialIndex: 0,
  lazyLoad: 2,
  pauseAutoPlayOnHover: true,
  prevNextButtons: false,
  pageDots: false,
  resize: true,
  selectedAttraction: 0.01,
  wrapAround: false
}

const sliderBeingDragged = 'slider-being-dragged'
function dragEnd() {
  document.ontouchmove = () => true
  setTimeout(() => {
    document.body.classList.remove(sliderBeingDragged)
  }, 50)
}

function dragMove() {
  document.ontouchmove = () => false
  document.body.classList.add(sliderBeingDragged)
}

function dragStart() {
  document.ontouchmove = () => iOS() ? false : false
}

const updateDots = (dotsContainerRef, flickityRef) => {
  const dots = dotsContainerRef.current?.children
  if (!dots || !flickityRef) return

  for (let i = 0; i < dots.length; i++) {
    const lines = dots[i].querySelectorAll('line')
    if (lines.length < 2) continue // Ensure we have at least 2 lines
    const progressLine = lines[1] // Second line for progress
    const isSelected = i === flickityRef.selectedIndex

    // Ensure the current active dot is animated
    if (isSelected) {
      progressLine.style.strokeOpacity = '1'
      progressLine.style.transition = `stroke-dasharray ${flickityRef.options.autoPlay || 4000}ms linear`
      progressLine.style.strokeDasharray = '62, 62' // Full progress
    }

    // Ensure all dots with 'viewed' class have correct styles
    if (dots[i].classList.contains('viewed')) {
      progressLine.style.strokeOpacity = '1'
      progressLine.style.strokeDasharray = '62, 62' // Full progress
      progressLine.style.transition = 'none' // No re-animation
    }

    // Reset unviewed, unselected dots
    if (!isSelected && !dots[i].classList.contains('viewed')) {
      progressLine.style.strokeOpacity = '0.4'
      progressLine.style.transition = 'none' // Avoid animation during reset
      progressLine.style.strokeDasharray = '0, 62' // Reset progress
    }
  }

  // On slide change, add 'viewed' class to the previous slide
  flickityRef.on('change', (currentIndex: number) => {
    const prevIndex = currentIndex === 0 ? dots.length - 1 : currentIndex - 1

    // Add 'viewed' class to the previous slide
    if (dots[prevIndex]) {
      dots[prevIndex].classList.add('viewed')

      // Explicitly ensure the styles are applied for the previous slide
      const lines = dots[prevIndex].querySelectorAll('line')
      const progressLine = lines[1]
      progressLine.style.strokeOpacity = '1'
      progressLine.style.strokeDasharray = '62, 62' // Full progress
      progressLine.style.transition = 'none' // No animation
    }

    // Reset all dots if looping back to the start
    if (currentIndex === 0) {
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.remove('viewed')
        const lines = dots[i].querySelectorAll('line')
        const progressLine = lines[1]
        progressLine.style.strokeDasharray = '0, 62' // Reset progress
        progressLine.style.strokeOpacity = '0.4'
      }
      dots[0].classList.add('viewed') // Mark the first dot as viewed
    }
  })

  // // Ensure the first slide is marked as viewed on load
  // if (flickityRef.selectedIndex === 0) {
  //   dots[0].classList.add('viewed')
  // }
}
const handleDotClick = (index: number, flickityRef: FlickityInstance) => {
  if (flickityRef) {
    flickityRef.select(index)
    if (flickityRef.options.autoPlay) {
      flickityRef.stopPlayer()
      flickityRef.playPlayer()
    }
  }
}

export const SliderFlickity: FC<Props> = memo(({ children, className = '', elementType = 'div', options = {}, onChange = null, reloadOnUpdate = false, dynamicSlider = false, hasCustomDots = false, colorDots = 'white', reverse = false, customAutoPlay = false, customAutoPlaySpeed = 1 }) => {
  const [flickityRef, setFlickityRef] = useState<FlickityInstance | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const dotsContainerRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number | undefined>(undefined)

  // Viewport detection with 10% threshold and margin for smooth transitions
  const isInView = useInView(sliderRef, {
    amount: 0.1,
    margin: '50px',
    once: false
  })

  useEffect(() => {
    if (!flickityRef) return
    flickityRef.on('dragStart', dragStart)
    flickityRef.on('dragMove', dragMove)
    flickityRef.on('dragEnd', dragEnd)
    if (onChange) flickityRef.on('change', onChange)

    if (hasCustomDots) {
      flickityRef.on('select', updateDots)
      updateDots(dotsContainerRef, flickityRef)
    }

    return () => {
      flickityRef.off('dragStart', dragStart)
      flickityRef.off('dragMove', dragMove)
      flickityRef.off('dragEnd', dragEnd)
      if (hasCustomDots) flickityRef.off('select', updateDots)
      if (onChange) flickityRef.off('change', onChange)
    }
  }, [flickityRef, hasCustomDots, onChange])

  // Handle standard Flickity autoplay based on viewport visibility
  useEffect(() => {
    if (!flickityRef || !flickityRef.options.autoPlay) return

    if (isInView) {
      flickityRef.playPlayer()
    } else {
      flickityRef.stopPlayer()
    }
  }, [flickityRef, isInView])

  useEffect(() => {
    if (!flickityRef || !customAutoPlay) return

    // Check if content overflows the container
    const containerWidth = flickityRef.size.width
    const totalContentWidth = flickityRef.slideableWidth

    // Only animate if content is wider than container
    const needsAnimation = totalContentWidth > containerWidth
    setShouldAnimate(needsAnimation)
  }, [flickityRef, customAutoPlay, children])

  useEffect(() => {
    if (!customAutoPlay || !flickityRef || !shouldAnimate) return

    const speed = customAutoPlaySpeed || 1.5

    const animate = () => {
      // Stop animation if paused (hover) OR not in view - but keep loop alive
      if (isPaused || !isInView) {
        animationRef.current = requestAnimationFrame(animate) // Keep loop alive but idle
        return
      }

      const direction = reverse ? 1 : -1
      const currentProgress = flickityRef.x
      const slideableWidth = flickityRef.slideableWidth || 0
      // Calculate new position
      const newProgress = currentProgress + (direction * speed)

      // Use Flickity's native positioning that respects wrapAround
      if (!reverse && newProgress < -slideableWidth) {
        // Reset to start smoothly
        flickityRef.x = 0
        flickityRef.setTranslateX(0)
      } else if (reverse && newProgress > 0) {
        // Reset to end smoothly
        flickityRef.x = -slideableWidth
        flickityRef.setTranslateX(-slideableWidth)
      } else {
        // Normal movement - use Flickity's positioning
        flickityRef.x = newProgress
        flickityRef.setTranslateX(newProgress)
      }

      // Force Flickity to update its internal state
      flickityRef.dispatchEvent('scroll')

      animationRef.current = requestAnimationFrame(animate)
    }

    // start animation loop
    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [flickityRef, customAutoPlay, customAutoPlaySpeed, reverse, isPaused, shouldAnimate, isInView])


  useEffect(() => {
    if (!customAutoPlay || !sliderRef.current) return

    const slider = sliderRef.current

    const handleMouseEnter = () => {
      setIsPaused(true)
      if (flickityRef && animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        // Force Flickity to settle and update its internal positioning
        flickityRef.settle(flickityRef.x)
        // Dispatch scroll event to ensure all positioning is synchronized
        flickityRef.dispatchEvent('scroll')
      }
    }

    const handleMouseLeave = () => {
      setIsPaused(false)
      // Resume from current position - don't restore saved position
      // The animation loop will pick up from flickityRef.x automatically
    }

    slider.addEventListener('mouseenter', handleMouseEnter)
    slider.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      slider.removeEventListener('mouseenter', handleMouseEnter)
      slider.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [customAutoPlay, flickityRef]) // Remove savedPosition dependency

  useEffect(() => {
    if (!flickityRef || !customAutoPlay) return

    const checkContentOverflow = () => {
      const containerWidth = flickityRef.size.width
      const totalContentWidth = flickityRef.slideableWidth
      const needsAnimation = totalContentWidth > containerWidth
      setShouldAnimate(needsAnimation)
    }

    // Initial check
    checkContentOverflow()

    // Listen for resize events
    const handleResize = () => {
      flickityRef.resize()
      setTimeout(checkContentOverflow, 100) // Small delay to ensure resize is complete
    }

    window.addEventListener('resize', handleResize)
    flickityRef.on('ready', checkContentOverflow)
    flickityRef.on('cellSelect', checkContentOverflow)

    return () => {
      window.removeEventListener('resize', handleResize)
      flickityRef.off('ready', checkContentOverflow)
      flickityRef.off('cellSelect', checkContentOverflow)
    }
  }, [flickityRef, customAutoPlay])


  return (
    <div className='slider-wrapper' ref={sliderRef}>
      <Flickity
        flickityRef={(ref) => setFlickityRef(ref)}
        className={`slider ${className}`}
        elementType={elementType}
        options={{ ...defaultOptions, ...options }}
        disableImagesLoaded={false}
        reloadOnUpdate={reloadOnUpdate}
        static={!dynamicSlider}
      >
        {children}
      </Flickity>
      {hasCustomDots && <div className='custom-dots' ref={dotsContainerRef}>
        {colorDots === 'white' ?
          React.Children.map(children, (_, i) => (
            <svg key={i} xmlns='http://www.w3.org/2000/svg' width='50' height='3' viewBox='0 0 50 1.5' fill='none' onClick={() => handleDotClick(i, flickityRef)} style={{ cursor: 'pointer' }} >
              <line y1='1.5' x2='50' y2='1.5' stroke={colorDots} strokeOpacity='0.4' strokeWidth='2' />
              <line y1='1.5' x2='50' y2='1.5' stroke={colorDots} strokeWidth='2' strokeDasharray='0, 50' />
            </svg>
          )) :
          React.Children.map(children, (_, i) => (
            <svg key={i} xmlns='http://www.w3.org/2000/svg' width='62' height='3' viewBox='0 0 62 1.5' fill='none' onClick={() => handleDotClick(i, flickityRef)} style={{ cursor: 'pointer' }} >
              <line x1="0.333496" y1="0.75" x2="62" y2="0.75" stroke="#3C3C3B" strokeOpacity="0.1" strokeWidth="1.5" />
              <line x1="0.333496" y1="0.75" x2="62" y2="0.75" stroke="#3C3C3B" strokeOpacity="0.6" strokeWidth="1.5" />
            </svg>
          ))
        }
      </div>}
    </div>
  )
})
